import {
  Button,
  CalloutIcon,
  DropdownMenu,
  type IconName,
} from '@wandb/components';
import clsx from 'clsx';
import React from 'react';

import {CodeSnippet} from '../CodeSnippet';
import {
  BASIC_CHART_PANEL_ITEMS,
  CONFIGURE_METRIC_PLACEHOLDER_ICON,
  DEFAULT_PLACEHOLDER_ICON,
  getBasicChartPanelIcon,
  getBasicChartPanelLabel,
} from './basicChartPanelTypes';
import type {ArtifactVersionMetricChartType} from './types';

const NO_METRICS_CODE_SNIPPET = `with wandb.init(project="my-project") as run:
    run.log({"accuracy": 0.91, "loss": 0.12})
    artifact = wandb.Artifact("model", type="model")
    artifact.add_file("model.pkl")
    run.log_artifact(artifact)`;

export type ArtifactVersionMetricPlaceholderStateProps = {
  title?: string;
  icon?: IconName;
  message: React.ReactNode;
  children?: React.ReactNode;
};

export const ArtifactVersionMetricPlaceholderState = ({
  title,
  icon,
  message,
  children,
}: ArtifactVersionMetricPlaceholderStateProps) => {
  return (
    <div
      className="flex min-h-[260px] w-full flex-col items-center justify-center gap-16 rounded-lg p-24 text-center"
      data-test="artifact-version-metrics-configure-placeholder">
      <CalloutIcon
        icon={icon ?? DEFAULT_PLACEHOLDER_ICON}
        color="blue"
        size="md"
      />
      <div className="max-w-[680px] text-base text-wb-text-secondary dark:text-moon-300">
        {title && <p className="font-semibold">{title}</p>}
        <p>{message}</p>
      </div>
      {children}
    </div>
  );
};

/** Shown when a panel has no chart type configured yet. */
export const ArtifactVersionMetricConfigurePlaceholder: React.FC<{
  onSelectChartType?: (chartType: ArtifactVersionMetricChartType) => void;
}> = ({onSelectChartType}) => {
  const props: ArtifactVersionMetricPlaceholderStateProps = {
    icon: CONFIGURE_METRIC_PLACEHOLDER_ICON,
    message: 'Compare metric across versions visually',
    title: 'Add chart',
  };

  if (onSelectChartType == null) {
    return <ArtifactVersionMetricPlaceholderState {...props} />;
  }

  return (
    <ArtifactVersionMetricPlaceholderState {...props}>
      <DropdownMenu.Trigger>
        <Button variant="secondary" startIcon="add-new">
          Choose panel type
        </Button>
        <DropdownMenu.Content>
          {BASIC_CHART_PANEL_ITEMS.map(item => (
            <DropdownMenu.Item
              key={item.type}
              id={item.type}
              onAction={() => onSelectChartType(item.type)}>
              <DropdownMenu.ItemDecorationLeft
                iconProps={{icon: getBasicChartPanelIcon(item.type)}}
              />
              <DropdownMenu.ItemLabel>{item.label}</DropdownMenu.ItemLabel>
              <DropdownMenu.ItemDescription>
                {item.description}
              </DropdownMenu.ItemDescription>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Trigger>
    </ArtifactVersionMetricPlaceholderState>
  );
};

export const ArtifactVersionMetricNoMetricsEmptyState = () => {
  return (
    <ArtifactVersionMetricPlaceholderState
      title="No metrics to plot yet."
      message="Charts use numeric values logged from the runs that produced these versions. Log metrics like accuracy or loss with wandb.log() before logging an artifact, then add the version to this collection.">
      <div className="w-full max-w-[520px]">
        <CodeSnippet
          codeSnippet={NO_METRICS_CODE_SNIPPET}
          codeLanguage="python"
        />
      </div>
    </ArtifactVersionMetricPlaceholderState>
  );
};

export const ARTIFACT_PLACEHOLDER_LINK = (
  <>
    <span className="font-semibold">Link artifact versions</span> to this
    collection to start comparing them here.
  </>
);

export const ARTIFACT_PLACEHOLDER_SELECT = (
  <>
    <span className="font-semibold">Select artifact versions</span> to start
    comparing them here.
  </>
);

/**
 * Shown inside a configured chart card when there is no data to render yet
 * (e.g. no versions selected or no metric chosen). Clicking opens the editor.
 */
export const ArtifactVersionMetricEmptyState: React.FC<{
  chartType: ArtifactVersionMetricChartType;
  message: string;
  onPress?: () => void;
  onRetry?: () => void;
}> = ({chartType, message, onPress, onRetry}) => {
  const clickableProps =
    onPress == null
      ? {}
      : {
          role: 'button',
          tabIndex: 0,
          onClick: onPress,
          onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onPress();
            }
          },
        };

  return (
    <div
      {...clickableProps}
      aria-label={
        onPress == null
          ? undefined
          : `Edit ${getBasicChartPanelLabel(chartType)}`
      }
      data-test="artifact-version-metrics-empty-state"
      data-test-chart-type={chartType}
      className={clsx(
        'flex h-full w-full flex-col items-center justify-center gap-12 p-24 text-center',
        onPress != null && 'cursor-pointer hover:bg-wb-surface-hover'
      )}>
      <CalloutIcon
        icon={getBasicChartPanelIcon(chartType)}
        color="blue"
        size="md"
      />
      <p className="text-sm text-wb-text-secondary dark:text-moon-400">
        {message}
      </p>
      {onRetry != null && (
        <Button size="sm" variant="secondary" onPress={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
};
